import classNames from 'classnames';
import type { Op } from 'quill/core';
import React, { useState } from 'react';
import AnnotationIcon from './AnnotationIcon';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import { CREATION_ID } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import MagneticWallSheet from '../MagneticWallSheet';
import useMagneticWallSheetContext from '../useMagneticWallSheetContext';
import AnnotationCreate from './AnnotationCreate';
import AnnotationLink from './AnnotationLink';
import AnnotationPreview from './AnnotationPreview';
import * as styles from './AnnotationSheet.module.scss';

const ANNOTATION_FRAGMENT = gql(`
  fragment AnnotationSheet_annotation on AnnotationFields {
    id
    linkText
    ...AnnotationPreview_annotation
  }
`);

interface AnnotationSheetProps {
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  onClose: () => void;
  onInputChange: (content: Op[]) => void;
  canEdit: boolean;
  unsavedInputValue: Op[];
  createAnnotation: React.ComponentProps<
    typeof AnnotationCreate
  >['createAnnotation'];
  deleteAnnotation: () => void;
}

const AnnotationSheet = ({
  onClose,
  annotation,
  onInputChange,
  canEdit,
  unsavedInputValue,
  createAnnotation,
  deleteAnnotation,
}: AnnotationSheetProps) => {
  const [linkText, setLinkText] = useState(annotation.linkText ?? '');
  const trimmedLinkText = linkText.trim();
  const [link, setLink] = useState(annotation.link ?? '');
  const sanitizedLink = sanitizeAnnotationLink(link);

  const isNewAnnotation = annotation.id === CREATION_ID;
  const [isEditing, setIsEditing] = useState(isNewAnnotation);

  const onDelete = () => {
    deleteAnnotation();
    onClose();
  };

  const { highlighted } = useMagneticWallSheetContext({
    type: 'annotation',
    id: annotation.id,
  });

  return (
    <MagneticWallSheet
      title={isNewAnnotation ? 'New annotation' : 'Annotation'}
      onClose={onClose}
      headerContent={
        <>
          {highlighted && highlighted.length > 0 && (
            <div
              className={classNames(styles.contextContainer, {
                [styles.hasPreview]: !isEditing,
              })}
            >
              {highlighted}
            </div>
          )}
          {!isEditing && (
            <div className={styles.previewContainer}>
              <h5>Annotation</h5>
              <AnnotationPreview
                annotation={annotation}
                avatar={<AnnotationIcon />}
              />
            </div>
          )}
          {!isEditing && annotation.link && (
            <AnnotationLink
              link={annotation.link}
              text={annotation.linkText ?? ''}
            />
          )}
        </>
      }
      overflowMenu={
        !isEditing && canEdit ? (
          <OverflowMenu>
            <MenuItem
              icon={editIcon}
              onClick={() => {
                setIsEditing(true);
              }}
            >
              Edit annotation
            </MenuItem>
            <MenuItem icon={trashIcon} danger onClick={onDelete}>
              Delete annotation
            </MenuItem>
          </OverflowMenu>
        ) : null
      }
      inputRow={
        isEditing ? (
          <>
            {sanitizedLink && (
              <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
            )}
            <div className={styles.createContainer}>
              <AnnotationCreate
                annotationId={annotation.id}
                initialContent={
                  isEditing ? (annotation.content ?? []) : unsavedInputValue
                }
                onChange={onInputChange}
                onSaved={() => {
                  onInputChange([]);
                  setIsEditing(false);
                }}
                link={link}
                onChangeLink={setLink}
                linkText={linkText}
                onChangeLinkText={setLinkText}
                onCancel={() => setIsEditing(false)}
                createAnnotation={createAnnotation}
              />
            </div>
          </>
        ) : null
      }
    />
  );
};

export default AnnotationSheet;
